import { Copy, Link2, Mail, Plus, Users } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Badge, Section, WorkspaceSkeleton } from '../components/UI'
import { ActionToast } from '../components/ActionToast'
import { useTeamScope } from '../components/TeamScope'
import { formatDuration, formatRelativeTime, loadTodayOperations, type TeamPerson } from '../lib/liveOperations'
import { supabase } from '../lib/supabase'

type TeamOption = {
  id: string
  name: string
  planned_member_count: number
}

type ManagerMailbox = {
  id: string
  provider: 'google' | 'microsoft'
  email_address: string
  status: string
  mailbox_type: 'personal' | 'shared'
}

type AssignableEmployee = {
  user_id: string
  display_name: string | null
  account_email: string | null
  team_id: string | null
}

type SharedMailboxAssignment = {
  mailbox_id: string
  user_id: string
}

function employeeName(employee: AssignableEmployee) {
  return employee.display_name || employee.account_email?.split('@')[0] || 'Employee'
}

function employeeInitials(employee: AssignableEmployee) {
  const name = employeeName(employee)
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('') || 'MP'
}

export function Team() {
  const { selectedTeamId, selectedTeam } = useTeamScope()
  const [people, setPeople] = useState<TeamPerson[]>([])
  const [teams, setTeams] = useState<TeamOption[]>([])
  const [role, setRole] = useState('')
  const [organisationId, setOrganisationId] = useState('')
  const [mailboxes, setMailboxes] = useState<ManagerMailbox[]>([])
  const [employees, setEmployees] = useState<AssignableEmployee[]>([])
  const [assignments, setAssignments] = useState<SharedMailboxAssignment[]>([])
  const [mailboxBusy, setMailboxBusy] = useState('')
  const [employeeTeamBusy, setEmployeeTeamBusy] = useState('')
  const [inviteTeam, setInviteTeam] = useState('')
  const [inviteLink, setInviteLink] = useState('')
  const [inviteExpiresAt, setInviteExpiresAt] = useState('')
  const [inviteBusy, setInviteBusy] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [toastTitle, setToastTitle] = useState('Team updated')

  const canManageAccess = ['owner', 'admin', 'manager'].includes(role)

  async function loadMailboxAccess(targetOrganisationId: string) {
    if (!supabase || !targetOrganisationId) return

    const [{ data: mailboxRows, error: mailboxError }, { data: employeeRows, error: employeeError }, { data: assignmentRows, error: assignmentError }] = await Promise.all([
      supabase
        .from('mailboxes')
        .select('id,provider,email_address,status,mailbox_type')
        .eq('organisation_id', targetOrganisationId)
        .eq('status', 'connected')
        .order('created_at', { ascending: true }),
      supabase
        .from('employee_profiles')
        .select('user_id,display_name,account_email,team_id')
        .eq('organisation_id', targetOrganisationId)
        .order('display_name', { ascending: true }),
      supabase
        .from('shared_mailbox_assignments')
        .select('mailbox_id,user_id')
    ])

    if (mailboxError) throw mailboxError
    if (employeeError) throw employeeError
    if (assignmentError) throw assignmentError

    setMailboxes((mailboxRows ?? []) as ManagerMailbox[])
    setEmployees((employeeRows ?? []) as AssignableEmployee[])
    setAssignments((assignmentRows ?? []) as SharedMailboxAssignment[])
  }

  useEffect(() => {
    async function load() {
      try {
        const data = await loadTodayOperations(selectedTeamId)
        setPeople(data.people)

        if (!supabase) return

        const { data: authData } = await supabase.auth.getUser()
        if (!authData.user) return

        const { data: membership } = await supabase
          .from('memberships')
          .select('organisation_id,role')
          .eq('user_id', authData.user.id)
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle()

        if (!membership?.organisation_id) return

        setRole(membership.role)
        setOrganisationId(membership.organisation_id)

        const { data: teamRows } = await supabase
          .from('teams')
          .select('id,name,planned_member_count')
          .eq('organisation_id', membership.organisation_id)
          .order('created_at', { ascending: true })

        const loadedTeams = (teamRows ?? []) as TeamOption[]
        setTeams(loadedTeams)
        setInviteTeam(selectedTeamId ?? loadedTeams[0]?.id ?? '')

        if (['owner', 'admin', 'manager'].includes(membership.role)) {
          await loadMailboxAccess(membership.organisation_id)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load team activity.')
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [selectedTeamId])

  async function createInvite() {
    if (!supabase || !inviteTeam) return

    setInviteBusy(true)
    setError('')
    setMessage('')
    setInviteLink('')
    setInviteExpiresAt('')

    const { data, error: inviteError } = await supabase.rpc('create_employee_invite_link', {
      p_team_id: inviteTeam
    })

    setInviteBusy(false)

    if (inviteError) {
      setError(inviteError.message)
      return
    }

    const result = data?.[0]
    const token = result?.invitation_token

    if (!token) {
      setError('Mail Percent could not generate the employee invitation link.')
      return
    }

    const link = `${window.location.origin}/join?token=${encodeURIComponent(token)}`
    setInviteLink(link)
    setInviteExpiresAt(String(result?.expires_at ?? ''))
    setToastTitle('Employee invitation link')
    setMessage('Invitation link generated. Send this link to employees who should join the selected team.')
  }

  async function copyInvite() {
    if (!inviteLink) return
    await navigator.clipboard.writeText(inviteLink)
    setToastTitle('Employee invitation link')
    setMessage('Employee invitation link copied.')
  }

  async function setMailboxAssignment(mailbox: ManagerMailbox, employee: AssignableEmployee, assigned: boolean) {
    if (!supabase || !organisationId) return

    const actionKey = `${mailbox.id}:${employee.user_id}`
    setMailboxBusy(actionKey)
    setError('')

    const { error: assignmentError } = await supabase.rpc('set_shared_mailbox_employee_assignment', {
      p_mailbox_id: mailbox.id,
      p_user_id: employee.user_id,
      p_assigned: assigned
    })

    if (assignmentError) {
      setMailboxBusy('')
      setError(assignmentError.message)
      return
    }

    try {
      await loadMailboxAccess(organisationId)
      setToastTitle('Shared mailbox access')
      setMessage(
        assigned
          ? `${employeeName(employee)} can now work from ${mailbox.email_address}.`
          : `${employeeName(employee)} no longer has ${mailbox.email_address} assigned.`
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Shared mailbox access was updated, but the latest setup could not be reloaded.')
    } finally {
      setMailboxBusy('')
    }
  }


  async function setEmployeeTeam(employee: AssignableEmployee, teamId: string) {
    if (!supabase || !teamId) return

    setEmployeeTeamBusy(employee.user_id)
    setError('')

    const { error: teamError } = await supabase.rpc('set_employee_primary_team', {
      p_user_id: employee.user_id,
      p_team_id: teamId
    })

    if (teamError) {
      setEmployeeTeamBusy('')
      setError(teamError.message)
      return
    }

    setEmployees(current => current.map(item => item.user_id === employee.user_id ? { ...item, team_id: teamId } : item))
    setToastTitle('Team membership')
    setMessage(`${employeeName(employee)} was moved to ${teams.find(team => team.id === teamId)?.name ?? 'the selected team'}.`)
    setEmployeeTeamBusy('')
  }

  const canInvite = canManageAccess

  if (loading) return <WorkspaceSkeleton rows={3}/>

  return (
    <div className="stack-lg team-page">
      {error && <div className="notice notice-inline"><strong>Team action unavailable:</strong><span>{error}</span></div>}
      {message && <ActionToast title={toastTitle} message={message} onDismiss={() => setMessage('')}/>}

      {canInvite && (
        <Section
          title="Employee invitations"
          subtitle="Generate a secure team link, send it to employees, and let each employee enter their own work email before connecting Gmail or Outlook"
          action={
            <button type="button" className="button primary" onClick={createInvite} disabled={inviteBusy || !inviteTeam}>
              <Link2 size={16}/>{inviteBusy ? 'Generating…' : inviteLink ? 'Generate new link' : 'Generate invitation link'}
            </button>
          }
        >
          <div className="workspace-code-panel employee-invite-link-panel">
            <div className="workspace-code-copy">
              <div className="employee-invite-heading">
                <span className="eyebrow">Shareable employee link</span>
                <strong>Employees join themselves</strong>
              </div>
              <p>Choose the team this link should join. Employees open the link, enter their own work email, create or sign into Mail Percent, and then connect their Gmail or Outlook mailbox once from the Employee Workspace.</p>

              <label className="field employee-invite-team-field" htmlFor="invite-team">
                <span>Team employees will join</span>
                <select id="invite-team" value={inviteTeam} onChange={event => { setInviteTeam(event.target.value); setInviteLink(''); setInviteExpiresAt('') }}>
                  {teams.map(team => <option key={team.id} value={team.id}>{team.name}</option>)}
                </select>
              </label>
            </div>

            <div className="workspace-code-steps">
              <strong className="workspace-code-steps-title">Employees will:</strong>
              <span><strong>1</strong>Open the invitation link</span>
              <span><strong>2</strong>Enter their work email</span>
              <span><strong>3</strong>Create or sign in to their employee account</span>
              <span><strong>4</strong>Connect Gmail or Outlook once</span>
            </div>
          </div>

          {inviteLink && (
            <div className="notice employee-invite-link-result">
              <div style={{minWidth: 0, flex: 1}}>
                <strong>Employee invitation link</strong>
                <span style={{wordBreak: 'break-all'}}>{inviteLink}</span>
                {inviteExpiresAt && <small className="muted">Valid until {new Date(inviteExpiresAt).toLocaleDateString()} unless you generate a replacement link.</small>}
              </div>
              <button type="button" className="button ghost" onClick={copyInvite}>
                <Copy size={16}/>Copy link
              </button>
            </div>
          )}
        </Section>
      )}

      {canManageAccess && (
        <>
        <Section
          title="Team membership"
          subtitle={selectedTeam ? `Plan and place employees for ${selectedTeam.name}` : 'Place each employee into the customer-email team they work in'}
        >
          <div className="team-membership-summary">
            <div><span>Selected view</span><strong>{selectedTeam?.name ?? 'All teams'}</strong></div>
            <div><span>Planned members</span><strong>{selectedTeam?.planned_member_count ?? (teams.length ? teams.reduce((sum, team) => sum + Number(team.planned_member_count || 0), 0) : '—')}</strong></div>
            <div><span>Employees joined</span><strong>{selectedTeamId ? employees.filter(employee => employee.team_id === selectedTeamId).length : employees.length}</strong></div>
          </div>
        </Section>
        <details className="team-placement-disclosure">
          <summary>Manage employee team placement</summary>
          {employees.length === 0 ? <div className="shared-mailbox-no-employees">Employees will appear here after they join the organisation.</div> : (
            <div className="team-membership-list">
              {employees.map(employee => (
                <div className="team-membership-row" key={employee.user_id}>
                  <span className="shared-mailbox-avatar">{employeeInitials(employee)}</span>
                  <div className="team-membership-person"><strong>{employeeName(employee)}</strong><span>{employee.account_email || 'Mail Percent employee'}</span></div>
                  <label>
                    <span>Team</span>
                    <select value={employee.team_id ?? ''} disabled={employeeTeamBusy === employee.user_id} onChange={event => setEmployeeTeam(employee, event.target.value)}>
                      <option value="" disabled>Choose team</option>
                      {teams.map(team => <option key={team.id} value={team.id}>{team.name}</option>)}
                    </select>
                  </label>
                </div>
              ))}
            </div>
          )}
        </details>
        </>
      )}

      {canManageAccess && (
        <Section
          title="Shared mailbox access"
          subtitle="Connect each shared customer inbox once, then choose which employees are allowed to work from it"
          action={
            <Link className="button secondary" to="/app/integrations">
              <Plus size={16}/>Connect mailbox
            </Link>
          }
        >
          <div className="shared-mailbox-manager-note">
            <Mail size={19}/>
            <div>
              <strong>Employees do not reconnect shared inboxes</strong>
              <span>Connect a shared customer inbox once, then assign the employees who work from it. Personal employee inboxes are connected by each employee from their own workspace.</span>
            </div>
          </div>

          {mailboxes.length === 0 ? (
            <div className="shared-mailbox-empty">
              <Users size={22}/>
              <div>
                <strong>No connected mailboxes yet</strong>
                <span>Connect the shared Gmail or Outlook mailbox in Integrations, then return here to assign employees.</span>
              </div>
            </div>
          ) : (
            <div className="shared-mailbox-manager-grid" tabIndex={0} role="region" aria-label="Shared mailboxes — scroll horizontally">
              {mailboxes.map(mailbox => {
                const assignedUserIds = new Set(
                  assignments
                    .filter(assignment => assignment.mailbox_id === mailbox.id)
                    .map(assignment => assignment.user_id)
                )

                return (
                  <article className="shared-mailbox-manager-card" key={mailbox.id}>
                    <div className="shared-mailbox-manager-head">
                      <div className={`provider tiny ${mailbox.provider === 'google' ? 'google' : 'microsoft'}`}>
                        <img
                          src={mailbox.provider === 'google' ? '/images/integrations/gmail-logo.png' : '/images/integrations/outlook-logo.png'}
                          alt={mailbox.provider === 'google' ? 'Gmail' : 'Microsoft Outlook'}
                        />
                      </div>
                      <div>
                        <strong>{mailbox.email_address}</strong>
                        <span>{mailbox.provider === 'google' ? 'Gmail' : 'Microsoft Outlook'}</span>
                      </div>
                      <Badge tone={mailbox.mailbox_type === 'shared' ? 'good' : 'neutral'}>
                        {mailbox.mailbox_type === 'shared' ? 'Shared' : 'Not shared yet'}
                      </Badge>
                    </div>

                    <p className="shared-mailbox-manager-helper">
                      {mailbox.mailbox_type === 'shared'
                        ? `${assignedUserIds.size} employee${assignedUserIds.size === 1 ? '' : 's'} assigned. Choose who should have access.`
                        : 'Assigning the first employee will automatically turn this connection into a shared mailbox.'}
                    </p>

                    {employees.length === 0 ? (
                      <div className="shared-mailbox-no-employees">
                        Invite employees using the employee invitation link above before assigning shared inbox access.
                      </div>
                    ) : (
                      <div className="shared-mailbox-employee-list" tabIndex={0} role="region" aria-label="Employees available for mailbox assignment — scroll horizontally">
                        {employees.map(employee => {
                          const checked = assignedUserIds.has(employee.user_id)
                          const actionKey = `${mailbox.id}:${employee.user_id}`
                          const busy = mailboxBusy === actionKey

                          return (
                            <label className={`shared-mailbox-employee ${checked ? 'selected' : ''}`} key={employee.user_id}>
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={busy}
                                onChange={event => setMailboxAssignment(mailbox, employee, event.target.checked)}
                              />
                              <span className="shared-mailbox-avatar">{employeeInitials(employee)}</span>
                              <span className="shared-mailbox-employee-copy">
                                <strong>{employeeName(employee)}</strong>
                                <small>{employee.account_email || 'Mail Percent employee'}</small>
                              </span>
                              <span className="shared-mailbox-assignment-state">{busy ? 'Saving…' : checked ? 'Assigned' : 'Assign'}</span>
                            </label>
                          )
                        })}
                      </div>
                    )}
                  </article>
                )
              })}
            </div>
          )}
        </Section>
      )}

      <Section
        title="Today by team member"
        subtitle="Measured workload, replies and open work for the current operating day"
      >
        <div className="split-card-grid" tabIndex={0} role="region" aria-label="Team members — scroll horizontally">
          {people.map(person =>
            <Link to={`/app/team/${person.userId}`} className="split-reveal-card" key={person.userId} aria-label={`Open ${person.displayName} employee page`}>
              <div className="split-reveal-identity">
                <div className="avatar split-avatar">{person.initials}</div>
                <div>
                  <strong>{person.displayName}</strong>
                  <span>{person.teamName}</span>
                </div>
                <Badge tone={person.status === 'recent' ? 'good' : 'neutral'}>{person.status === 'recent' ? 'Recent data' : person.status === 'earlier' ? 'Earlier data' : 'No data'}</Badge>
              </div>

              <div className="split-reveal-stats">
                <div className="split-stat-reveal">
                  <div><span>Customer emails worked</span><strong>{person.threadsWorked}</strong></div>
                  <div><span>Customer replies sent</span><strong>{person.replied}</strong></div>
                  <div><span>Still needs follow-through</span><strong>{person.open}</strong></div>
                  <div><span>Response rate</span><strong>{person.threadsWorked ? `${Math.round((person.replied / person.threadsWorked) * 100)}%` : '—'}</strong></div>
                </div>
                <div className="split-reveal-foot">
                  <span>Avg response {formatDuration(person.averageQueueWaitSeconds)}</span>
                  <span>{formatRelativeTime(person.lastActivityAt)}</span>
                </div>
              </div>
            </Link>
          )}

          {people.length === 0 && <p className="muted">No customer email activity has been measured for the team yet today.</p>}
        </div>
      </Section>
    </div>
  )
}
